import { DiagramCanvas } from "@/features/diagram/DiagramCanvas";
import {
  useEventFlowDiagram,
  usePluginMap,
  useSystemDiagram,
  useWorkflowDiagram,
} from "@/features/diagram/hooks/useSystemDiagram";
import { useBreadcrumbs } from "@/lib/use-breadcrumbs";
import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import { useTranslation } from "react-i18next";

export const Route = createFileRoute("/settings/diagram")({
  component: SettingsDiagramPage,
});

type View = "overview" | "plugins" | "workflow" | "events";

const VIEWS: { value: View; label: string }[] = [
  { value: "overview", label: "Обзор системы" },
  { value: "plugins", label: "Плагины" },
  { value: "workflow", label: "Процессы" },
  { value: "events", label: "Потоки событий" },
];

const WORKFLOWS = [
  { value: "commerce.checkout", label: "Оформление заказа" },
  { value: "commerce.fulfillment", label: "Фулфилмент" },
  { value: "commerce.quote_conversion", label: "Конвертация КП" },
  { value: "order_lifecycle", label: "Жизненный цикл заказа" },
  { value: "accounting.post_journal_entry", label: "Бухгалтерская проводка" },
  { value: "accounting.record_payment", label: "Учёт оплаты" },
  { value: "dynamic_pipeline", label: "Динамический пайплайн" },
  { value: "enrichment_single", label: "Обогащение товара" },
];

const PLUGIN_DOMAINS = [
  { value: "all", label: "Все домены" },
  { value: "payment", label: "Оплата" },
  { value: "delivery", label: "Доставка" },
  { value: "enrichment", label: "Обогащение" },
  { value: "mrp", label: "Планирование" },
  { value: "accounting", label: "Бухгалтерия" },
];

function SettingsDiagramPage() {
  const { t } = useTranslation("settings");
  const [view, setView] = useState<View>("overview");
  const [selectedWorkflow, setSelectedWorkflow] = useState("commerce.checkout");
  const [pluginDomain, setPluginDomain] = useState("all");

  useBreadcrumbs([
    { label: t("breadcrumb_settings"), href: "/settings" },
    { label: t("sidebar_diagram") },
  ]);

  // Build selectors config for the canvas toolbar
  const selectors = [
    { value: view, options: VIEWS, onChange: (v: string) => setView(v as View) },
    ...(view === "workflow"
      ? [{ value: selectedWorkflow, options: WORKFLOWS, onChange: setSelectedWorkflow }]
      : []),
    ...(view === "plugins"
      ? [{ value: pluginDomain, options: PLUGIN_DOMAINS, onChange: setPluginDomain }]
      : []),
  ];

  return (
    <div className="-mx-6 -mt-4 -mb-6" style={{ height: "calc(100vh - 5.5rem)" }}>
      {view === "overview" && <OverviewView selectors={selectors} />}
      {view === "plugins" && <PluginsView domain={pluginDomain === "all" ? "" : pluginDomain} selectors={selectors} />}
      {view === "workflow" && <WorkflowView name={selectedWorkflow} selectors={selectors} />}
      {view === "events" && <EventsView selectors={selectors} />}
    </div>
  );
}

type SelectorConfig = { value: string; options: { value: string; label: string }[]; onChange: (v: string) => void };

function OverviewView({ selectors }: { selectors: SelectorConfig[] }) {
  const { data, isLoading } = useSystemDiagram({
    includeWorkflows: false,
    includePlugins: true,
    includeEventFlows: false,
    includeServices: false,
  });
  return <DiagramCanvas graph={data} isLoading={isLoading} selectors={selectors} />;
}

function PluginsView({ domain, selectors }: { domain: string; selectors: SelectorConfig[] }) {
  const { data, isLoading } = usePluginMap(domain || undefined);
  return <DiagramCanvas graph={data} isLoading={isLoading} selectors={selectors} />;
}

function WorkflowView({ name, selectors }: { name: string; selectors: SelectorConfig[] }) {
  const { data, isLoading } = useWorkflowDiagram(name);
  return <DiagramCanvas graph={data} isLoading={isLoading} selectors={selectors} />;
}

function EventsView({ selectors }: { selectors: SelectorConfig[] }) {
  const { data, isLoading } = useEventFlowDiagram();
  return <DiagramCanvas graph={data} isLoading={isLoading} selectors={selectors} />;
}
